import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import Label from 'LuxKit/Label'
import type { ComponentProps } from 'react'
import styled from 'styled-components'

const TierLabel = styled(Label)`
  &.kind-primary {
    &.tier-bronze {
      background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
      &.gradient {
        background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
        color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
      }
    }

    &.tier-silver {
      background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
      &.gradient {
        background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
        color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
      }
    }

    &.tier-gold {
      background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
      &.gradient {
        background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
        color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
      }
    }

    &.tier-platinum {
      background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
      &.gradient {
        background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
        color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
      }
    }

    &.tier-infinite {
      background-color: ${props => props.theme.palette.product.loyalty.infinite.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.infinite.darkContrast};
      &.gradient {
        background: ${props => props.theme.palette.product.loyalty.infinite.gradientPrimaryBackground};
        color: ${props => props.theme.palette.product.loyalty.infinite.normalContrast};
      }
    }
  }

  &.kind-secondary {
    &.tier-bronze {
      background-color: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
      border-color: ${props => props.theme.palette.product.loyalty.bronze.normalForeground};
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    }

    &.tier-silver {
      background-color: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
      border-color: ${props => props.theme.palette.product.loyalty.silver.normalForeground};
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    }

    &.tier-gold {
      background-color: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
      border-color: ${props => props.theme.palette.product.loyalty.gold.normalForeground};
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    }

    &.tier-platinum {
      background-color: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
      border-color: ${props => props.theme.palette.product.loyalty.platinum.normalForeground};
      color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
    }

    &.tier-infinite {
      background-color: ${props => props.theme.palette.product.loyalty.infinite.normalBackground};
      border-color: ${props => props.theme.palette.product.loyalty.infinite.normalForeground};
      color: ${props => props.theme.palette.product.loyalty.infinite.normalContrast};
    }
  }

  &.kind-tertiary {
    &.tier-bronze {
      background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
      border-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    }

    &.tier-silver {
      background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
      border-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    }

    &.tier-gold {
      background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
      border-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    }

    &.tier-platinum {
      background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
      border-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast};
    }

    &.tier-infinite {
      background-color: ${props => props.theme.palette.product.loyalty.infinite.lightBackground};
      border-color: ${props => props.theme.palette.product.loyalty.infinite.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.infinite.lightContrast};
    }
  }
`

type InheritedLabelProps = Pick<ComponentProps<typeof Label>, 'children' | 'startIcon' | 'format'>

interface Props extends InheritedLabelProps {
  /**
   * @default user's tier
   */
  tier?: App.LoyaltyTier
  kind: 'primary' | 'secondary' | 'tertiary'
  gradient?: boolean
}

function LoyaltyTierLabel({ tier, kind, gradient, ...rest }: Props) {
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  return <TierLabel
    {...rest}
    className={clsx(`tier-${tier ?? displayTier}`, { gradient })}
    kind={kind}
    variant="grey"
  />
}

export default LoyaltyTierLabel
